import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PlacementAnalyticsService from './PlacementAnalyticsService';
import { directoryByCompanyPath, watchlistDashboardPath, isDirectoryLinkEnabled, type PlacementRoleBase } from './deepLinks';
import './placementAnalytics.css';

interface Props {
    instituteId: number | string;
    roleBase: PlacementRoleBase;
}

const RANKING_ORDER = [
    'ALUMNI_INFLUENCE',
    'HIRING_DEPTH',
    'MEAN_SALARY',
    'AVG_OFFERS_PER_VISIT',
    'LOYALTY_SCORE',
] as const;

const RANKING_LABELS: Record<string, string> = {
    ALUMNI_INFLUENCE: 'Alumni Influence Score',
    HIRING_DEPTH: 'Hiring Depth Score',
    MEAN_SALARY: 'Mean Salary (₹L)',
    AVG_OFFERS_PER_VISIT: 'Avg Offers / Visiting Year',
    LOYALTY_SCORE: 'Loyalty Score (0–100)',
};

const formatRankingValue = (type: string, value: any) => {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (type === 'MEAN_SALARY') {
        return Number.isFinite(n) ? `₹${n.toFixed(1)}L` : String(value);
    }
    return Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : n.toFixed(1)) : String(value);
};

const rankMedalClass = (rank: number) => {
    if (rank === 1) return 'pi-rank-gold';
    if (rank === 2) return 'pi-rank-silver';
    if (rank === 3) return 'pi-rank-bronze';
    return 'pi-rank-plain';
};

const seniorityTotal = (row: any) => {
    const f = Number(row.foundersCount) || 0;
    const el = Number(row.execLeadershipCount) || 0;
    const sm = Number(row.seniorMgmtCount) || 0;
    const mid = Number(row.midMgmtCount) || 0;
    const o = Number(row.managerOthersCount) || 0;
    return f * 4 + el * 3.5 + sm * 3 + mid * 2 + o * 1;
};

const formatSeniority = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

const influenceClass = (cat?: string) => {
    const c = String(cat || '').toUpperCase();
    if (c.includes('HIGH')) return 'pi-inf-high';
    if (c.includes('MED')) return 'pi-inf-med';
    if (c.includes('LOW')) return 'pi-inf-low';
    return 'pi-inf-med';
};

const CompanyLink = ({
    companyId,
    name,
    enabled,
}: {
    companyId?: number | null;
    name?: string;
    enabled: boolean;
}) => {
    if (!companyId || !enabled) return <span>{name || '—'}</span>;
    return (
        <Link to={directoryByCompanyPath(companyId)} className="pi-company-link">
            {name || companyId}
        </Link>
    );
};

const PlacementInsights = ({ instituteId, roleBase }: Props) => {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        PlacementAnalyticsService.getInsights(instituteId)
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load insights');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [instituteId]);

    const genuineSorted = useMemo(() => {
        const rows = [...(data?.sections?.untappedGenuine || [])];
        rows.sort((a, b) => seniorityTotal(b) - seniorityTotal(a));
        return rows;
    }, [data]);

    const psuSorted = useMemo(() => {
        const rows = [...(data?.sections?.untappedPsuGovt || [])];
        rows.sort((a, b) => seniorityTotal(b) - seniorityTotal(a));
        return rows;
    }, [data]);

    if (loading) return <div className="text-muted font-13">Loading insights…</div>;
    if (error) return <div className="alert alert-danger py-2 font-13">{error}</div>;

    const toolbarActions = (
        <div className="d-flex align-items-center flex-wrap gap-2">
            <Link to={watchlistDashboardPath(roleBase, instituteId)} className="pi-watchlist-btn">
                Open Watchlist Dashboard
            </Link>
        </div>
    );

    if (!data?.uploadId) {
        return (
            <div className="pi-insights">
                <div className="pi-toolbar">
                    <div />
                    {toolbarActions}
                </div>
                <div className="alert alert-info py-2 font-13">No successful ANALYSIS upload yet.</div>
            </div>
        );
    }

    const stats = data.quickStats || {};
    const sections = data.sections || {};
    const dirEnabled = isDirectoryLinkEnabled(roleBase);
    const yearFrom = data.yearFrom != null && String(data.yearFrom).trim() !== '' ? String(data.yearFrom) : '';
    const yearTo = data.yearTo != null && String(data.yearTo).trim() !== '' ? String(data.yearTo) : '';
    const yearRange = yearFrom && yearTo ? `${yearFrom}–${yearTo}` : yearFrom || yearTo;
    const yearLabel = yearRange ? `${yearRange} dataset` : '';

    const cards = [
        {
            key: 'visited',
            label: 'Visited Companies',
            value: stats.visitedCompanies ?? 0,
            sub: yearLabel,
            accent: 'blue',
        },
        {
            key: 'genuine',
            label: 'Genuine Untapped',
            value: stats.genuineUntapped ?? 0,
            sub: 'High/Mod influence · Never recruited',
            accent: 'yellow',
        },
        {
            key: 'psu',
            label: 'PSU / Govt Untapped',
            value: stats.psuGovtUntapped ?? 0,
            sub: 'Exec programme — not outreach targets',
            accent: 'cyan',
        },
        {
            key: 'retention',
            label: 'Low Retention',
            value: stats.lowRetention ?? 0,
            sub: 'Ratio < 0.3 · Min 3 offers',
            accent: 'red',
        },
        {
            key: 'salary',
            label: 'Top Salary',
            value: stats.topSalary?.value != null ? `₹${Number(stats.topSalary.value).toFixed(2)}L` : '—',
            sub: stats.topSalary?.companyName || '',
            accent: 'green',
            companyId: stats.topSalary?.companyId,
        },
    ];

    return (
        <div className="pi-insights">
            <div className="pi-toolbar">
                {yearRange ? <div className="pi-meta">Years {yearRange}</div> : <div />}
                {toolbarActions}
            </div>

            <div className="pi-stat-row">
                {cards.map((c) => (
                    <div className={`pi-stat-card pi-accent-${c.accent}`} key={c.key}>
                        <div className="pi-stat-label">{c.label}</div>
                        <div className="pi-stat-value">{c.value}</div>
                        <div className="pi-stat-sub">
                            {c.key === 'salary' && c.companyId ? (
                                <CompanyLink companyId={c.companyId} name={c.sub} enabled={dirEnabled} />
                            ) : (
                                c.sub || '\u00a0'
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {genuineSorted.length > 0 && (
                <section className="pi-section">
                    <div className="pi-section-head">
                        <div className="pi-section-title-row">
                            <h3 className="pi-section-title">Untapped Companies — Genuine Outreach Targets</h3>
                            <span className="pi-count-pill">{stats.genuineUntapped ?? genuineSorted.length}</span>
                        </div>
                        <p className="pi-section-sub">
                            High/Moderate influence alumni companies that have NEVER recruited on campus.
                        </p>
                    </div>

                    <div className="pi-table-card">
                        <div className="pi-table-banner">
                            <span>Private Sector · High &amp; Medium Influence</span>
                            <span className="pi-count-pill pi-count-pill-dark">
                                {stats.genuineUntapped ?? genuineSorted.length} companies
                            </span>
                        </div>
                        <div className="pi-table-scroll">
                            <table className="pi-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Company</th>
                                        <th>Influence</th>
                                        <th>Founders</th>
                                        <th>Senior Mgmt</th>
                                        <th>Mid Mgmt</th>
                                        <th>Mgr &amp; Others</th>
                                        <th>Total Alumni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {genuineSorted.map((r: any, idx: number) => (
                                        <tr key={r.companyId || idx}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <CompanyLink companyId={r.companyId} name={r.companyName} enabled={dirEnabled} />
                                            </td>
                                            <td>
                                                <span className={`pi-inf-badge ${influenceClass(r.influenceCategory)}`}>
                                                    {r.influenceCategory || '—'}
                                                </span>
                                            </td>
                                            <td>{r.foundersCount ?? 0}</td>
                                            <td>{r.seniorMgmtCount ?? 0}</td>
                                            <td>{r.midMgmtCount ?? 0}</td>
                                            <td>{r.managerOthersCount ?? 0}</td>
                                            <td className="pi-strong">
                                                {dirEnabled && r.companyId ? (
                                                    <Link
                                                        to={directoryByCompanyPath(r.companyId)}
                                                        className="pi-company-link">
                                                        {r.totalAlumniPresent ?? 0}
                                                    </Link>
                                                ) : (
                                                    r.totalAlumniPresent ?? 0
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {psuSorted.length > 0 && (
                <section className="pi-section">
                    <div className="pi-section-head">
                        <div className="pi-section-title-row">
                            <h3 className="pi-section-title">Untapped PSU/Govt/Non-Placement Companies</h3>
                            <span className="pi-count-pill">{stats.psuGovtUntapped ?? psuSorted.length}</span>
                        </div>
                        <p className="pi-section-sub">
                            Executive programme alumni at PSUs. NOT active placement targets.
                        </p>
                    </div>
                    <div className="pi-table-card">
                        <div className="pi-table-banner">
                            <span>PSU &amp; Govt Organisations</span>
                            <span className="pi-count-pill pi-count-pill-dark">
                                {stats.psuGovtUntapped ?? psuSorted.length} companies
                            </span>
                        </div>
                        <div className="pi-table-scroll">
                            <table className="pi-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Organisation</th>
                                        <th>Influence</th>
                                        <th>Total Alumni</th>
                                        <th>Seniority Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {psuSorted.map((r: any, idx: number) => (
                                        <tr key={r.companyId || idx}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <CompanyLink companyId={r.companyId} name={r.companyName} enabled={dirEnabled} />
                                            </td>
                                            <td>
                                                <span className={`pi-inf-badge ${influenceClass(r.influenceCategory)}`}>
                                                    {r.influenceCategory || '—'}
                                                </span>
                                            </td>
                                            <td className="pi-strong">{r.totalAlumniPresent ?? 0}</td>
                                            <td className="pi-strong">{formatSeniority(seniorityTotal(r))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {sections.retention?.length > 0 && (
                <section className="pi-section">
                    <div className="pi-section-head">
                        <div className="pi-section-title-row">
                            <h3 className="pi-section-title">Retention Analysis</h3>
                            <span className="pi-count-pill">{sections.retention.length}</span>
                        </div>
                        <p className="pi-section-sub">Ratio &lt; 0.3 · Min 3 offers (lowest ratios shown)</p>
                    </div>
                    <div className="pi-table-card">
                        <div className="pi-table-scroll">
                            <table className="pi-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Company</th>
                                        <th>Visits</th>
                                        <th>Offers</th>
                                        <th>Current Alumni</th>
                                        <th>Ratio</th>
                                        <th>Category</th>
                                        <th>Recruiting?</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sections.retention.map((r: any, idx: number) => (
                                        <tr key={r.companyId || idx}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <CompanyLink companyId={r.companyId} name={r.companyName} enabled={dirEnabled} />
                                            </td>
                                            <td>{r.visits ?? '—'}</td>
                                            <td>{r.totalOffers ?? '—'}</td>
                                            <td>{r.currentAlumniCount ?? '—'}</td>
                                            <td>{r.retentionRatio ?? '—'}</td>
                                            <td>{r.category ?? '—'}</td>
                                            <td>
                                                {r.stillRecruiting == null ? '—' : r.stillRecruiting ? 'Yes' : 'No'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {sections.rankings &&
                (() => {
                    const rankingMap: Record<string, any[]> = {};
                    for (const type of RANKING_ORDER) {
                        const rows =
                            sections.rankings[type] ||
                            sections.rankings[type.replace(/_/g, '')] ||
                            [];
                        if (rows.length) rankingMap[type] = rows;
                    }
                    // Fallback: accept whatever keys API returned
                    if (!Object.keys(rankingMap).length) {
                        for (const [k, rows] of Object.entries(sections.rankings)) {
                            if (Array.isArray(rows) && rows.length) rankingMap[k] = rows as any[];
                        }
                    }
                    if (!Object.keys(rankingMap).length) return null;
                    return (
                    <section className="pi-section">
                        <div className="pi-section-head">
                            <div className="pi-section-title-row">
                                <h3 className="pi-section-title">All Rankings — Top 25</h3>
                            </div>
                        </div>
                        <div className="pi-rankings-grid">
                            {(Object.keys(rankingMap).length === RANKING_ORDER.length
                                ? RANKING_ORDER
                                : Object.keys(rankingMap)
                            ).map((type) => {
                                const rows = rankingMap[type] || [];
                                if (!rows.length) return null;
                                return (
                                    <div className="pi-ranking-card" key={type}>
                                        <div className="pi-ranking-head">{RANKING_LABELS[type] || type}</div>
                                        <ol className="pi-ranking-list">
                                            {rows.slice(0, 25).map((r: any) => {
                                                const rank = Number(r.rankPosition) || 0;
                                                return (
                                                    <li key={`${type}-${rank}-${r.companyId}`}>
                                                        <span className={`pi-rank-num ${rankMedalClass(rank)}`}>
                                                            {rank}
                                                        </span>
                                                        <span className="pi-rank-name" title={r.companyName || ''}>
                                                            <CompanyLink
                                                                companyId={r.companyId}
                                                                name={r.companyName}
                                                                enabled={dirEnabled}
                                                            />
                                                        </span>
                                                        <span className="pi-rank-value">
                                                            {formatRankingValue(type, r.value)}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    );
                })()}
        </div>
    );
};

export default PlacementInsights;
